import { observer } from 'mobx-react'
import React, { Key, useState } from 'react'

import { PipelineModel } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'
import {
   Badge,
   CustomOptionType,
   Item,
   Picker,
   PickerContextProvider
} from '@flowwai-frontend/design-system'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import {
   ChevronDownIcon,
   FilterLinesIcon,
   TickMark
} from '@flowwai-frontend/design-system/icons'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'

import {
   getItemTextWithIconContainerThemeStyles,
   getSalesPipelineCustomOptionThemeStyles,
   tickIconClassName
} from './styles'
import './styles.css'

interface Props {
   selectedPipelineId: string
   setSelectedPipelineId: (id: string) => void
   pipelines: PipelineModel[]
}

const GSPipelinePicker = (props: Props) => {
   const { selectedPipelineId, setSelectedPipelineId, pipelines } = props

   const [isOpenPopOver, setIsOpenPopOver] = useState(false)

   const t = useCustomT('globalSearch:searchModal')
   const { theme } = useAppTheme()
   const { salesPipelinePickerTheme } = theme.globalSearch.searchModal

   const onChangePipeline = (id: Key) => {
      setSelectedPipelineId(id as string)
   }

   const renderRightIcon = (color: string): React.ReactElement =>
      isOpenPopOver ? (
         <div
            style={{
               transform: 'rotate(180deg)',
               transition: 'transform 0.5s',
               flexShrink: 0
            }}
         >
            <ChevronDownIcon height={12} width={12} fill={color} />
         </div>
      ) : (
         <div style={{ transition: 'transform 0.5s', flexShrink: 0 }}>
            <ChevronDownIcon height={12} width={12} fill={color} />
         </div>
      )

   const renderCustomOption = (
      props: CustomOptionType<any>
   ): React.ReactElement => {
      const { item, isSelected, optionRef, optionProps } = props

      return (
         <div
            {...optionProps}
            ref={optionRef}
            className={getItemTextWithIconContainerThemeStyles(
               salesPipelinePickerTheme,
               isSelected
            )}
         >
            <span
               className={getSalesPipelineCustomOptionThemeStyles(
                  salesPipelinePickerTheme.salesPipelineColor
               )}
               title={item.textValue}
            >
               {item.textValue}
            </span>
            {isSelected ? (
               <div className={tickIconClassName}>
                  <TickMark
                     fill={salesPipelinePickerTheme.tickIconColor}
                     width={20}
                     height={20}
                  />
               </div>
            ) : null}
         </div>
      )
   }

   const renderValueContainer = () => {
      const pipelineDisplayName = pipelines.find(
         pipeline => pipeline.pipelineId === selectedPipelineId
      )?.displayName

      return (
         <Badge
            size='Small'
            variant={'PRIMARY'}
            rightElement={renderRightIcon}
            leftElement={color => (
               <FilterLinesIcon width={12} height={12} stroke={color} />
            )}
            isOutline={true}
            containerClassName='flex-block'
            badgeTextClassName='truncate value-container-badge-styles'
         >
            {t('pipelineSelector', {
               pipelineName: pipelineDisplayName
            })}
         </Badge>
      )
   }

   const popOverStyles = {
      width: '242px',
      marginTop: '5px',
      boxShadow:
         ' 0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)'
   }

   const onOpenChange = (value: boolean) => {
      setIsOpenPopOver(value)
   }

   return (
      <PickerContextProvider renderValueContainer={renderValueContainer}>
         <Picker
            selectedKey={selectedPipelineId}
            onSelectionChange={onChangePipeline}
            customOption={(args: CustomOptionType<any>) =>
               renderCustomOption(args)
            }
            size={'Small'}
            focusRingVariant='None'
            buttonClassName={`pipeline-picker`}
            pickerButtonClassName='w-[100%]'
            popOverStyles={popOverStyles}
            showPickerButtonRightIcon={false}
            onOpenChange={onOpenChange}
            showLabel={false}
         >
            {pipelines.map(option => (
               <Item key={option.pipelineId}>{option.displayName}</Item>
            ))}
         </Picker>
      </PickerContextProvider>
   )
}

export default observer(GSPipelinePicker)
